import AbstractUnit from './AbstractUnit';
import { COLOR_THEMES } from '../../../../constants/colors';
import { FONTS } from '../../../../constants/fonts';
import Text3D from '../../objects/Text3D';
import {
    REF_SIZE, REF_SIZE_PT, SIZE_OPTIONS, UNIT
} from '../../../../constants/size';
import { TextUnitStyle } from '../../../../constants/UnitTypes';
import { linear, clamp01 } from '../../../../utils/progress';

const THREE = require('three');

const getStrongMetrics = (orientation) => {
    if (orientation === SIZE_OPTIONS.LANDSCAPE) {
        return {
            isUpperCase: true,
            fontFamily: FONTS.REGULAR,
            fontSize: 84,
            lineHeight: 72 / 84,
            width: 9.8 * 84
        };
    }

    return {
        isUpperCase: true,
        fontFamily: FONTS.REGULAR,
        fontSize: 85,
        lineHeight: 102 / 90,
        width: 9.8 * 85
    };
};

const getEmphasisMetrics = (orientation) => {
    if (orientation === SIZE_OPTIONS.LANDSCAPE) {
        return {
            fontFamily: FONTS.REGULAR,
            fontSize: 111,
            lineHeight: 50 / 67,
            width: REF_SIZE.w * 0.55,
            align: 'left'
        };
    }

    return {
        fontFamily: FONTS.REGULAR,
        fontSize: 122,
        lineHeight: 50 / 67,
        width: REF_SIZE_PT.w * 0.8,
        align: 'left'
    };
};

const getDefaultMetrics = (orientation, isShort = false) => {
    if (orientation === SIZE_OPTIONS.LANDSCAPE) {
        return {
            fontFamily: FONTS.REGULAR,
            fontSize: 50,
            lineHeight: isShort ? 1 : 47 / 30,
            width: REF_SIZE.w * 0.55,
            align: 'left'
        };
    }

    return {
        fontFamily: FONTS.REGULAR,
        fontSize: 55,
        lineHeight: isShort ? 1 : 47 / 30,
        width: REF_SIZE_PT.w * 0.8,
        align: 'left'
    };
};

const getMetrics = (style, orientation) => {
    if (style === TextUnitStyle.STRONG) {
        return getStrongMetrics(orientation);
    }

    if (style === TextUnitStyle.EMPHASIS) {
        return getEmphasisMetrics(orientation);
    }

    if (style === TextUnitStyle.SHORT) {
        return getDefaultMetrics(orientation, true);
    }

    return getDefaultMetrics(orientation);
};

const getAnimationMaterialType = () => ({
    material: {
        type: 'textWaveMaterial'
    },
    isAnimation: true
});
/* if (style === TextUnitStyle.STRONG) {
        return {};
    }

    return {
        material: {
            type: 'textWaveMaterial'
        },
        isAnimation: true
    }; */

class TextUnit extends AbstractUnit {
    constructor(data, webgl, markers) {
        super(data, webgl, null, null, markers);
    }

    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        const {
            theme, text, textPlane, style
        } = this.data;

        this.hasInvalidate = true;
        this.hasResize = true;
        this._color = COLOR_THEMES[theme].text;
        this._text = (textPlane ?? text).join('');
        this._style = style ?? '';

        // Default
        this._orientation = SIZE_OPTIONS.LANDSCAPE;
    }

    _constructView(data, webgl, stgs, factory, orientation) {
        const metrics = getMetrics(this._style, orientation);

        let { scale } = this.data;
        const { widthRatio } = this.data;

        metrics.width *= widthRatio ?? 1;

        const mesh = new Text3D({
            ...metrics,
            color: this._color
        }, this._text, webgl, getAnimationMaterialType(this._style));
        const root = new THREE.Group();

        scale = scale ?? 1;

        root.scale.set(scale, scale, scale);
        root.position.z = -this.mainViewZ;
        root.add(mesh);
        this.add(root);

        this.textMesh = mesh;
        this.mainView = root;

        this.hasProgressAnimation = metrics.hasShiftProgress;
    }

    resize(winWidth, winHeight, orientation, force) {
        if (this._orientation === orientation && !force) return;

        const { widthRatio } = this.data;
        const metrics = getMetrics(this._style, orientation);

        if (orientation === SIZE_OPTIONS.LANDSCAPE) metrics.width *= widthRatio ?? 1;

        this.textMesh.setMetrics({
            ...metrics
        });

        let { position, anchor } = this.data;
        const sizeRef = orientation === SIZE_OPTIONS.LANDSCAPE ? REF_SIZE : REF_SIZE_PT;
        const sizesScale = UNIT.sizesScaleRatio;

        position = position ?? { x: 0, y: 0 };
        anchor = { x: 0.5, y: 0.5, ...(anchor ?? {}) };

        let x = position.x * sizeRef.w * sizeRef.ratio.x;
        let y = position.y * sizeRef.h * sizeRef.ratio.y;

        if (winWidth < 800) {
            if (anchor.x < 0.1 && x > 0) anchor.x = 0.25;
        }

        if (orientation === SIZE_OPTIONS.PORTRAIT) {
            anchor.x = 0.5;
            x = 0;
            y *= 0.8;
        }

        this.textMesh.position.set(
            metrics.width * sizesScale * (0.5 - anchor.x),
            this.textMesh.textHeight * (anchor.y - 0.5),
            0
        );

        this.mainView.position.set(
            x,
            y,
            -this.mainViewZ
        );

        this._orientation = orientation;
    }

    invalidate(t, dt) {
        super.invalidate(t, dt);
        this.textMesh.invalidate(t, dt);
    }

    _updateAnimation(newProgress) {
        if (this._style === TextUnitStyle.STRONG) this.textMesh.progress = 1 - clamp01(linear(newProgress, -2.8, -1.9));

        this.textMesh.progress = 0;
    }
}

export default TextUnit;
